import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, ShieldCheck } from "lucide-react";
import { FadeUp, SectionHead } from "./Reveal";

export function CheckGrid({ items, light = false, cols = 2 }) {
    return (
        <ul className={`grid grid-cols-1 gap-x-8 gap-y-3 ${cols === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2"}`}>
            {items.map((item) => (
                <li key={item} className={`flex items-start gap-2.5 text-sm ${light ? "text-navy-deep/75" : "text-slate-300/85"}`}>
                    <Check size={14} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                    {item}
                </li>
            ))}
        </ul>
    );
}

export function NumberSteps({ steps, light = false }) {
    return (
        <div>
            {steps.map((s, i) => {
                const step = typeof s === "string" ? { title: s } : s;
                return (
                    <FadeUp key={step.title} delay={i * 0.05}>
                        <div className={`grid grid-cols-1 items-baseline gap-2 border-t py-7 md:grid-cols-12 md:gap-6 ${light ? "border-navy-deep/10" : "border-white/10"}`}>
                            <span className="font-serif text-4xl font-light leading-none text-outline-gold md:col-span-2" aria-hidden="true">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <h3 className={`font-serif text-xl font-normal md:col-span-4 lg:text-2xl ${light ? "text-navy-deep" : "text-ivory"}`}>
                                {step.title}
                            </h3>
                            {step.copy && (
                                <p className={`max-w-xl text-sm leading-relaxed md:col-span-6 md:justify-self-end ${light ? "text-navy-deep/70" : "text-slate-300/80"}`}>
                                    {step.copy}
                                </p>
                            )}
                        </div>
                    </FadeUp>
                );
            })}
            <div className={`border-t ${light ? "border-navy-deep/10" : "border-white/10"}`} aria-hidden="true" />
        </div>
    );
}

export function InfoCards({ cards, light = false, cols = 3 }) {
    return (
        <div className={`grid grid-cols-1 gap-5 sm:grid-cols-2 ${cols === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
            {cards.map((c, i) => (
                <FadeUp key={c.title} delay={(i % 3) * 0.07} className="h-full">
                    <div
                        className={`h-full border p-7 transition-colors duration-500 hover:border-gold/40 ${
                            light ? "border-navy-deep/10 bg-white" : "border-white/8 bg-navy-surface"
                        }`}
                    >
                        <h3 className={`font-serif text-xl font-normal lg:text-2xl ${light ? "text-navy-deep" : "text-ivory"}`}>
                            {c.title}
                        </h3>
                        {c.copy && (
                            <p className={`mt-3 text-sm leading-relaxed ${light ? "text-navy-deep/70" : "text-slate-300/80"}`}>
                                {c.copy}
                            </p>
                        )}
                    </div>
                </FadeUp>
            ))}
        </div>
    );
}

export function DisclaimerBox({ text, testid = "disclaimer-box" }) {
    return (
        <FadeUp>
            <div className="flex items-start gap-4 border border-gold/30 bg-gold/5 p-6 lg:p-7" data-testid={testid}>
                <ShieldCheck size={20} className="mt-0.5 shrink-0 text-gold" aria-hidden="true" />
                <p className="text-sm leading-relaxed text-gold-bright/90">{text}</p>
            </div>
        </FadeUp>
    );
}

export function CTABand({ title, primary, secondary }) {
    const navigate = useNavigate();
    return (
        <FadeUp>
            <div className="flex flex-col items-start gap-8 border border-gold/25 bg-gradient-to-b from-navy-surface to-navy-deep p-10 lg:flex-row lg:items-center lg:justify-between lg:p-14">
                <h2 className="max-w-xl font-serif text-3xl font-light leading-tight text-ivory lg:text-4xl">{title}</h2>
                <div className="flex shrink-0 flex-wrap gap-4">
                    {primary && (
                        <button
                            type="button"
                            data-testid={primary.testid || "cta-primary"}
                            onClick={() => navigate(primary.to)}
                            className="group inline-flex items-center gap-3 bg-gold px-7 py-4 text-xs font-bold uppercase tracking-[0.18em] text-navy-deep transition-colors duration-300 hover:bg-gold-bright"
                        >
                            {primary.label}
                            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    )}
                    {secondary && (
                        <button
                            type="button"
                            data-testid={secondary.testid || "cta-secondary"}
                            onClick={() => navigate(secondary.to)}
                            className="inline-flex items-center gap-3 border border-white/25 px-7 py-4 text-xs font-bold uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:border-gold hover:text-gold"
                        >
                            {secondary.label}
                        </button>
                    )}
                </div>
            </div>
        </FadeUp>
    );
}

export function PageSection({ eyebrow, title, copy, light = false, children, className = "", testid }) {
    return (
        <section className={`${light ? "bg-ivory text-navy-deep" : "bg-navy-deep"} py-20 lg:py-28 ${className}`} data-testid={testid}>
            <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                {(eyebrow || title) && <SectionHead light={light} eyebrow={eyebrow} title={title} copy={copy} />}
                <div className={eyebrow || title ? "mt-12" : ""}>{children}</div>
            </div>
        </section>
    );
}
